import React, { useState, useEffect, useRef } from "react";
import { Plus, Edit2, Trash2, Save, X, Image, Loader2 } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Simple Content Editable Editor - supports paste from Word/Google Docs
const ContentEditor = ({ value, onChange, onImageUpload }) => {
  const editorRef = useRef(null);
  const fileInputRef = useRef(null);
  const isInitialized = useRef(false);

  useEffect(() => {
    if (editorRef.current) {
      // Always update on value change (handles edit mode)
      if (!isInitialized.current || editorRef.current !== document.activeElement) {
        editorRef.current.innerHTML = value || "";
        isInitialized.current = true;
      }
    }
  }, [value]);

  const handleInput = () => {
    if (editorRef.current) {
      onChange(editorRef.current.innerHTML);
    }
  };

  const handlePaste = (e) => {
    // Allow paste with formatting from Word/Google Docs
    // The browser will handle the HTML conversion automatically
  };

  const handleImageInsert = async () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (file && onImageUpload) {
      const url = await onImageUpload(file);
      if (url && editorRef.current) {
        // Insert image at cursor position
        const img = `<img src="${url}" alt="image" style="max-width: 100%; border-radius: 8px; margin: 10px 0;" />`;
        document.execCommand('insertHTML', false, img);
        handleInput();
      }
    }
    e.target.value = '';
  };

  return (
    <div className="border rounded-lg overflow-hidden">
      {/* Simple toolbar - just image insert */}
      <div className="flex items-center gap-2 p-2 bg-muted/30 border-b">
        <Button 
          type="button" 
          variant="outline" 
          size="sm" 
          onClick={handleImageInsert}
          className="text-xs"
        >
          <Image className="w-4 h-4 mr-1" />
          Insérer une image
        </Button>
        <span className="text-xs text-muted-foreground ml-2">
          Collez directement depuis Word ou Google Docs
        </span>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileChange}
          className="hidden"
        />
      </div>

      {/* Content Editable Area */}
      <div
        ref={editorRef}
        contentEditable
        onInput={handleInput}
        onPaste={handlePaste}
        className="p-4 min-h-[250px] bg-white text-gray-800 focus:outline-none
          [&_h1]:text-xl [&_h1]:font-bold [&_h1]:mb-3 [&_h1]:mt-4
          [&_h2]:text-lg [&_h2]:font-bold [&_h2]:mb-2 [&_h2]:mt-3
          [&_h3]:text-base [&_h3]:font-bold [&_h3]:mb-2 [&_h3]:mt-2
          [&_p]:mb-3 [&_p]:leading-relaxed
          [&_strong]:font-bold [&_b]:font-bold
          [&_em]:italic [&_i]:italic
          [&_u]:underline
          [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:mb-3
          [&_ol]:list-decimal [&_ol]:pl-6 [&_ol]:mb-3
          [&_li]:mb-1
          [&_img]:max-w-full [&_img]:rounded-lg [&_img]:my-3
          [&_a]:text-blue-600 [&_a]:underline
          [&_blockquote]:border-l-4 [&_blockquote]:border-gray-300 [&_blockquote]:pl-4 [&_blockquote]:italic [&_blockquote]:my-3"
        style={{ minHeight: '250px' }}
        data-placeholder="Écrivez ou collez votre contenu ici..."
      />
    </div>
  );
};

export const AdminContentManager = () => {
  const [contents, setContents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [editingContent, setEditingContent] = useState(null);
  const [uploading, setUploading] = useState(false);
  const mainImageRef = useRef(null);
  
  const [formData, setFormData] = useState({
    category: "Hip-Hop/RNB",
    title: "",
    content: "",
    author: "TIKA Team",
    image: ""
  });

  useEffect(() => {
    fetchContents();
  }, []);

  const fetchContents = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("tika_admin_token");
      const response = await axios.get(`${API}/admin/culture?limit=100`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setContents(response.data || []);
    } catch (error) {
      console.error("Erreur:", error);
      toast.error("Erreur lors du chargement");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleContentChange = (value) => {
    setFormData({ ...formData, content: value });
  };

  const handleCategoryChange = (value) => {
    setFormData({ ...formData, category: value });
  };

  const uploadImage = async (file) => {
    if (file.size > 5 * 1024 * 1024) {
      toast.error("L'image ne doit pas dépasser 5MB");
      return null;
    }

    try {
      const uploadFormData = new FormData();
      uploadFormData.append("file", file);

      const response = await axios.post(`${API}/upload`, uploadFormData, {
        headers: { "Content-Type": "multipart/form-data" }
      });

      if (response.data.url) {
        toast.success("Image uploadée !");
        return `${BACKEND_URL}${response.data.url}`;
      }
    } catch (error) {
      console.error("Erreur upload:", error);
      toast.error("Erreur lors de l'upload");
    }
    return null;
  };

  const handleMainImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    const url = await uploadImage(file);
    if (url) {
      setFormData({ ...formData, image: url });
    }
    setUploading(false);
  };

  const handleRemoveImage = () => {
    setFormData({ ...formData, image: "" });
    if (mainImageRef.current) mainImageRef.current.value = "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title || !formData.content) {
      toast.error("Veuillez remplir le titre et le contenu");
      return;
    }

    try {
      const token = localStorage.getItem("tika_admin_token");
      const headers = { 
        Authorization: `Bearer ${token}`,
        "Content-Type": "application/json"
      };

      const dataToSend = {
        category: formData.category,
        title: formData.title,
        content: formData.content,
        author: formData.author,
        image: formData.image || ""
      };

      if (editingContent) {
        await axios.put(
          `${API}/admin/culture/${editingContent.id}`,
          dataToSend,
          { headers }
        );
        toast.success("Article mis à jour !");
      } else {
        await axios.post(`${API}/admin/culture`, dataToSend, { headers });
        toast.success("Article créé !");
      }

      // Reset form
      setFormData({
        category: "Hip-Hop/RNB",
        title: "",
        content: "",
        author: "TIKA Team",
        image: ""
      });
      setIsEditing(false);
      setEditingContent(null);
      
      await fetchContents();
    } catch (error) {
      console.error("Erreur:", error);
      toast.error(error.response?.data?.detail || "Erreur lors de l'enregistrement");
    }
  };

  const handleEdit = (content) => {
    setEditingContent(content);
    setFormData({
      category: content.category,
      title: content.title,
      content: content.content,
      author: content.author || "TIKA Team",
      image: content.image || ""
    });
    setIsEditing(true);
    // Scroll to top to show the form
    setTimeout(() => {
      document.querySelector('[data-testid="content-editor-form"]')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);
  };

  const handleDelete = async (contentId) => {
    if (!window.confirm("Supprimer cet article ?")) return;

    try {
      const token = localStorage.getItem("tika_admin_token");
      await axios.delete(`${API}/admin/culture/${contentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Article supprimé");
      fetchContents();
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditingContent(null);
    setFormData({
      category: "Hip-Hop/RNB",
      title: "",
      content: "",
      author: "TIKA Team",
      image: ""
    });
  };

  const stripHtml = (html) => {
    const tmp = document.createElement("div");
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || "";
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold">Articles Culturels</h2>
        {!isEditing && (
          <Button onClick={() => setIsEditing(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Nouvel Article
          </Button>
        )}
      </div>

      {/* Form */}
      {isEditing && (
        <form onSubmit={handleSubmit} className="glass-card rounded-lg p-6 space-y-4" data-testid="content-editor-form">
          <h3 className="text-lg font-bold">
            {editingContent ? "Modifier l'article" : "Nouvel article"}
          </h3>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label>Catégorie *</Label>
              <Select value={formData.category} onValueChange={handleCategoryChange}>
                <SelectTrigger className="mt-2">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Hip-Hop/RNB">Hip-Hop/RNB</SelectItem>
                  <SelectItem value="Afrobeat">Afrobeat</SelectItem>
                  <SelectItem value="Dancehall/Caraïbes">Dancehall/Caraïbes</SelectItem>
                  <SelectItem value="Général">Général</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Auteur</Label>
              <Input
                name="author"
                value={formData.author}
                onChange={handleInputChange}
                placeholder="TIKA Team"
                className="mt-2"
              />
            </div>
          </div>

          <div>
            <Label>Titre *</Label>
            <Input
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              placeholder="Titre de l'article"
              required
              className="mt-2"
            />
          </div>

          {/* Main Image */}
          <div>
            <Label>Image principale (miniature)</Label>
            <div className="mt-2 flex items-center gap-3">
              {formData.image ? (
                <div className="relative w-20 h-20 rounded-lg overflow-hidden bg-muted flex-shrink-0">
                  <img src={formData.image} alt="" className="w-full h-full object-cover" />
                  <Button
                    type="button"
                    variant="destructive"
                    size="icon"
                    className="absolute top-1 right-1 w-5 h-5"
                    onClick={handleRemoveImage}
                  >
                    <X className="w-3 h-3" />
                  </Button>
                </div>
              ) : (
                <div
                  className="w-20 h-20 border-2 border-dashed rounded-lg flex flex-col items-center justify-center cursor-pointer hover:border-primary/50"
                  onClick={() => mainImageRef.current?.click()}
                >
                  {uploading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <Image className="w-5 h-5 text-muted-foreground" />
                  )}
                </div>
              )}
              <input
                ref={mainImageRef}
                type="file"
                accept="image/*"
                onChange={handleMainImageUpload}
                className="hidden"
              />
              <Input
                name="image"
                value={formData.image}
                onChange={handleInputChange}
                placeholder="Ou coller une URL..."
                className="flex-1"
              />
            </div>
          </div>

          {/* Content Editor */}
          <div>
            <Label>Contenu *</Label>
            <p className="text-xs text-muted-foreground mb-2">
              Vous pouvez copier/coller depuis Word ou Google Docs avec la mise en forme
            </p>
            <ContentEditor 
              value={formData.content} 
              onChange={handleContentChange}
              onImageUpload={uploadImage}
            />
          </div>

          <div className="flex gap-3 pt-2">
            <Button type="submit" disabled={uploading}>
              <Save className="w-4 h-4 mr-2" />
              {editingContent ? "Mettre à jour" : "Créer"}
            </Button>
            <Button type="button" variant="outline" onClick={handleCancel}>
              <X className="w-4 h-4 mr-2" />
              Annuler
            </Button>
          </div>
        </form>
      )}

      {/* Content List */}
      <div className="space-y-3">
        {contents.length === 0 ? (
          <div className="text-center py-12 text-muted-foreground">
            Aucun article. Créez-en un !
          </div>
        ) : (
          contents.map((content) => (
            <div key={content.id} className="glass-card rounded-lg p-4 flex items-start justify-between gap-4">
              <div className="flex gap-3 flex-1 min-w-0">
                {content.image && (
                  <img src={content.image} alt="" className="w-12 h-12 rounded-lg object-cover flex-shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="px-2 py-0.5 bg-primary/20 text-primary text-xs font-semibold rounded">
                      {content.category}
                    </span>
                  </div>
                  <h4 className="font-semibold text-sm mb-1">{content.title}</h4>
                  <p className="text-xs text-muted-foreground line-clamp-2">
                    {stripHtml(content.content)}
                  </p>
                </div>
              </div>
              <div className="flex gap-1">
                <Button variant="ghost" size="icon" onClick={() => handleEdit(content)}>
                  <Edit2 className="w-4 h-4" />
                </Button>
                <Button variant="ghost" size="icon" onClick={() => handleDelete(content.id)}>
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};
